import { apiClient } from './client'

export type ConsolidationTerminalStatus = 'succeeded' | 'failed' | 'cancelled' | 'budget_exceeded'

export interface ConsolidationTerminalEvent {
  id: string
  type: `consolidation.${ConsolidationTerminalStatus}`
  occurredAt: string
  runId: number
  projectId: number
  projectName: string
  areaId: number
  areaName: string
  status: ConsolidationTerminalStatus
  errorMessage: string | null
}

function isConsolidationTerminalEvent(value: unknown): value is ConsolidationTerminalEvent {
  if (!value || typeof value !== 'object') return false
  const event = value as Partial<ConsolidationTerminalEvent>
  return (
    typeof event.id === 'string' &&
    typeof event.type === 'string' &&
    event.type.startsWith('consolidation.') &&
    typeof event.runId === 'number' &&
    typeof event.projectId === 'number' &&
    typeof event.projectName === 'string' &&
    typeof event.areaId === 'number' &&
    typeof event.areaName === 'string' &&
    typeof event.status === 'string' &&
    (event.errorMessage === null || typeof event.errorMessage === 'string')
  )
}

export class SseParser {
  private buffer = ''

  push(chunk: string): ConsolidationTerminalEvent[] {
    this.buffer += chunk.replace(/\r\n/g, '\n')
    const frames = this.buffer.split('\n\n')
    this.buffer = frames.pop() ?? ''
    const events: ConsolidationTerminalEvent[] = []

    for (const frame of frames) {
      if (!frame || frame.startsWith(':')) continue
      const data = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n')
      if (!data) continue
      try {
        const parsed: unknown = JSON.parse(data)
        if (isConsolidationTerminalEvent(parsed)) events.push(parsed)
      } catch {
        // A malformed event is isolated to its frame; the stream remains usable.
      }
    }
    return events
  }
}

export class RecentEventIds {
  private readonly ids = new Set<string>()

  constructor(private readonly limit = 200) {}

  accept(id: string): boolean {
    if (this.ids.has(id)) return false
    this.ids.add(id)
    if (this.ids.size > this.limit) this.ids.delete(this.ids.values().next().value as string)
    return true
  }
}

const reconnectDelays = [1_000, 2_000, 4_000, 8_000, 15_000]

function wait(delayMs: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, delayMs)
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer)
        resolve()
      },
      { once: true }
    )
  })
}

export async function listenForAppEvents(
  signal: AbortSignal,
  onEvent: (event: ConsolidationTerminalEvent) => void
): Promise<void> {
  const seen = new RecentEventIds()
  let retry = 0

  while (!signal.aborted) {
    try {
      const response = await apiClient.openStream('/events', signal)
      const reader = response.body?.getReader()
      if (!reader) throw new Error('Event stream response body is unavailable')
      const decoder = new TextDecoder()
      const parser = new SseParser()
      retry = 0
      while (!signal.aborted) {
        const { done, value } = await reader.read()
        if (done) break
        for (const event of parser.push(decoder.decode(value, { stream: true }))) {
          if (!seen.accept(event.id)) continue
          onEvent(event)
        }
      }
    } catch (error) {
      if (signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return
      console.warn('Application event stream disconnected; reconnecting', {
        error: error instanceof Error ? error.message : String(error),
      })
    }
    if (!signal.aborted) {
      await wait(reconnectDelays[Math.min(retry, reconnectDelays.length - 1)]!, signal)
      retry += 1
    }
  }
}
